import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  Check,
  User,
  FileText,
  Calendar,
  Paperclip,
  Edit3,
  Plus,
  History,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Building2,
  Send,
  AlertCircle,
  Loader2,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import './PatientDetail.css';
import AppointmentModal from './AppointmentModal';


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', background: '#ff000033', color: 'white', borderRadius: '8px', margin: '20px' }}>
          <h2>Algo salió mal al cargar esta sección.</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{this.state.error?.toString()}</pre>
          <button onClick={() => this.setState({hasError: false})} style={{ marginTop: '10px', padding: '5px 10px' }}>Reintentar</button>
        </div>
      );
    }
    return this.props.children;
  }
}


const PatientDetailInner = ({ patient, onClose }) => {
  // Load active staff
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff.full_name || 'GDT Admin';
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o' || staffName.toLowerCase().includes('administracion');

  const { addNotification } = useNotifications();
  const [currentPatient, setCurrentPatient] = useState(patient);
  const [activeTab, setActiveTab] = useState('evoluciones');
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [appModalData, setAppModalData] = useState(null);
  const [evolutions, setEvolutions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [professionals, setProfessionals] = useState([]);

  useEffect(() => {
    supabase.from('professionals').select('*').then(({data}) => {
       if (data) setProfessionals(data);
    });
  }, []);
  
  // New note creation state
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Edit states for Patient Datos Filiatorios
  const [isEditingDatos, setIsEditingDatos] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDni, setEditDni] = useState('');
  const [editBirthdate, setEditBirthdate] = useState('');
  const [editInsurance, setEditInsurance] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editAffiliateNumber, setEditAffiliateNumber] = useState('');
  const [editPatientType, setEditPatientType] = useState('Ambulatorio');
  const [editCity, setEditCity] = useState('');
  const [editProvince, setEditProvince] = useState('');
  const [editCountry, setEditCountry] = useState('Argentina');
  const [editLawyer, setEditLawyer] = useState('');
  const [editMedicalNotes, setEditMedicalNotes] = useState('');

  // Turnos history accordion and filter states
  const [turnosFilterTab, setTurnosFilterTab] = useState('todos');
  const [expandedAppId, setExpandedAppId] = useState(null);

  // Edit states for Medical Evolutions
  const [editingEvId, setEditingEvId] = useState(null);
  const [editingEvContent, setEditingEvContent] = useState('');
  const [savingEvChange, setSavingEvChange] = useState(false);

  useEffect(() => {
    if (patient) {
      const fetchFullPatient = async () => {
        try {
          let query = supabase.from('patients').select('*');
          if (patient.id) {
            query = query.eq('id', patient.id);
          } else if (patient.dni || patient.patient_dni) {
            query = query.eq('dni', String(patient.dni || patient.patient_dni).trim());
          } else if (patient.full_name || patient.patient_name) {
            query = query.ilike('full_name', (patient.full_name || patient.patient_name).trim());
          }

          const { data: fullPat } = await query.maybeSingle();
          if (fullPat) {
            setCurrentPatient(fullPat);
            if (fullPat.id) fetchEvolutions(fullPat.id);
            fetchAppointments(fullPat.dni, fullPat.id, fullPat.full_name);
          } else {
            setCurrentPatient(patient);
            if (patient.id) fetchEvolutions(patient.id);
            fetchAppointments(patient.dni || patient.patient_dni, patient.id, patient.full_name || patient.patient_name);
          }
        } catch (e) {
          console.error('Error fetching full patient details:', e);
          setCurrentPatient(patient);
          if (patient.id) fetchEvolutions(patient.id);
          fetchAppointments(patient.dni || patient.patient_dni, patient.id, patient.full_name || patient.patient_name);
        }
      };
      fetchFullPatient();
      setIsEditingDatos(false);
      setEditingEvId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  useEffect(() => {
    // Notify AICopilot about current patient context
    if (currentPatient && evolutions) {
      window.dispatchEvent(new CustomEvent('ai_patient_context', {
        detail: { patient: currentPatient, evolutions }
      }));
    }
    return () => {
      window.dispatchEvent(new CustomEvent('ai_patient_context', { detail: null }));
    };
  }, [currentPatient, evolutions]);

  async function fetchEvolutions(targetPatientId) {
    const patientId = targetPatientId !== undefined ? targetPatientId : currentPatient?.id;
    if (!patientId) {
      setEvolutions([]);
      return;
    }
    try {
      let query = supabase
        .from('medical_evolutions')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (staffName && staffName !== 'GDT Admin' && !isAdmin) {
        query = query.eq('professional_name', staffName);
      }

      const { data, error } = await query;
      if (error) console.error('fetchEvolutions error:', error);
      if (data) setEvolutions(data);
    } catch (e) {
      console.error('fetchEvolutions catch error:', e);
    }
  }

  async function fetchAppointments(targetDni, targetPatientId, targetFullName) {
    const patientDni = targetDni !== undefined ? targetDni : (currentPatient?.dni || patient?.dni || patient?.patient_dni);
    const patientId = targetPatientId !== undefined ? targetPatientId : (currentPatient?.id || patient?.id);
    const patientName = targetFullName !== undefined ? targetFullName : (currentPatient?.full_name || patient?.full_name || patient?.patient_name);

    if (!patientDni && !patientId && !patientName) {
      setAppointments([]);
      return;
    }
    setLoading(true);
    try {
      let allApps = [];

      // 1. By patient_id
      if (patientId) {
        let q1 = supabase.from('appointments').select('*').eq('patient_id', patientId).order('appointment_date', { ascending: false });
        if (staffName && staffName !== 'GDT Admin' && !isAdmin) q1 = q1.eq('professional_name', staffName);
        const { data: d1 } = await q1;
        if (d1) allApps.push(...d1);
      }

      // 2. By patient_dni
      if (patientDni && String(patientDni).trim()) {
        const normDni = String(patientDni).trim();
        let q2 = supabase.from('appointments').select('*').eq('patient_dni', normDni).order('appointment_date', { ascending: false });
        if (staffName && staffName !== 'GDT Admin' && !isAdmin) q2 = q2.eq('professional_name', staffName);
        const { data: d2 } = await q2;
        if (d2) allApps.push(...d2);

        let q3 = supabase.from('appointments').select('*').eq('study_details->>patient_dni', normDni).order('appointment_date', { ascending: false });
        if (staffName && staffName !== 'GDT Admin' && !isAdmin) q3 = q3.eq('professional_name', staffName);
        const { data: d3 } = await q3;
        if (d3) allApps.push(...d3);
      }

      // 3. By patient_name
      if (patientName && String(patientName).trim()) {
        const normName = String(patientName).trim();
        let q4 = supabase.from('appointments').select('*').ilike('patient_name', normName).order('appointment_date', { ascending: false });
        if (staffName && staffName !== 'GDT Admin' && !isAdmin) q4 = q4.eq('professional_name', staffName);
        const { data: d4 } = await q4;
        if (d4) allApps.push(...d4);
      }

      // Deduplicate by appointment ID
      const uniqueMap = new Map();
      allApps.forEach(a => {
        if (a && a.id && !uniqueMap.has(a.id)) {
          uniqueMap.set(a.id, a);
        }
      });

      const uniqueApps = Array.from(uniqueMap.values()).sort((a, b) => {
        const dateA = (a.appointment_date || '') + ' ' + (a.appointment_time || '');
        const dateB = (b.appointment_date || '') + ' ' + (b.appointment_time || '');
        return dateB.localeCompare(dateA);
      });

      setAppointments(uniqueApps);
    } catch (e) {
      console.error('fetchAppointments catch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvolution = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const { error } = await supabase
        .from('medical_evolutions')
        .insert([{
          patient_id: currentPatient.id,
          content: newNote,
          professional_name: staffName,
          created_by_user: (() => { const u = JSON.parse(localStorage.getItem('gdt_active_user') || '{}'); const s = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}'); return u.display_name || u.username || s.full_name || 'Sistema'; })(),
          created_at: new Date().toISOString()
        }]);
      
      if (error) throw error;
      addNotification('Evolución guardada', 'success');
      setNewNote('');
      fetchEvolutions();
    } catch (err) {
      addNotification('Error al guardar: ' + err.message, 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleNewAppointment = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setAppModalData({
      patient_id: currentPatient.id,
      patient: currentPatient.full_name,
      patient_dni: currentPatient.dni
    });
    setIsAppModalOpen(true);
  };

  const handleSaveAppointment = async (data) => {
    try {
      const { error } = await supabase.from('appointments').insert([{
        patient_id: data.patient_id || null,
        patient_dni: data.patient_dni || null,
        patient_name: data.patient,
        patient_email: data.patient_email || '',
        patient_phone: data.patient_phone || '',
        appointment_date: data.date,
        appointment_time: data.time,
        status: 'pendiente',
        admin_notes: data.notes,
        study_type: data.studyType || 'CONSULTA GENERAL',
        professional_name: data.professional,
        created_by_user: (() => { const u = JSON.parse(localStorage.getItem('gdt_active_user') || '{}'); const s = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}'); return u.display_name || u.username || staffName || s.full_name || 'Sistema'; })()
      }]);
      if (error) throw error;
      addNotification('Turno asignado correctamente', 'success');
      setIsAppModalOpen(false);
      fetchAppointments(currentPatient.dni);
    } catch (err) {
      addNotification('Error al asignar turno: ' + err.message, 'error');
    }
  };

  const handleStartEditingDatos = () => {
    setEditName(currentPatient.full_name || '');
    setEditDni(currentPatient.dni || '');
    setEditBirthdate(currentPatient.birthdate || '');
    setEditInsurance(currentPatient.insurance_provider || '');
    setEditPhone(currentPatient.phone || '');
    setEditEmail(currentPatient.email || '');
    setEditAddress(currentPatient.address || '');
    setEditAffiliateNumber(currentPatient.affiliate_number || '');
    setEditPatientType(currentPatient.patient_type || 'Ambulatorio');
    setEditCity(currentPatient.city || '');
    setEditProvince(currentPatient.province || '');
    setEditCountry(currentPatient.country || 'Argentina');
    setEditLawyer(currentPatient.preferences || currentPatient.lawyer || '');
    setEditMedicalNotes(currentPatient.medical_notes || '');
    setIsEditingDatos(true);
  };

  const [savingDatos, setSavingDatos] = useState(false);

  const handleSaveDatos = async () => {
    if (!editName.trim()) {
      addNotification('El Nombre Completo es obligatorio', 'error');
      return;
    }
    setSavingDatos(true);
    try {
      const payload = {
        full_name: editName.trim().toUpperCase(),
        dni: editDni.trim() || null,
        birthdate: editBirthdate || null,
        insurance_provider: editInsurance ? editInsurance.trim() : null,
        phone: editPhone ? editPhone.trim() : null,
        email: editEmail ? editEmail.trim() : null,
        address: editAddress ? editAddress.trim() : null,
        affiliate_number: editAffiliateNumber ? editAffiliateNumber.trim() : null,
        patient_type: editPatientType || 'Ambulatorio',
        city: editCity ? editCity.trim() : null,
        province: editProvince ? editProvince.trim() : null,
        country: editCountry ? editCountry.trim() : 'Argentina',
        preferences: editLawyer ? editLawyer.trim() : null,
        medical_notes: editMedicalNotes ? editMedicalNotes.trim() : null
      };

      const targetId = currentPatient?.id || currentPatient?.patient_id;
      const targetDni = currentPatient?.dni || editDni;

      let patientUpdated = false;
      let finalId = targetId;

      // 1. Try updating by ID if present
      if (targetId) {
        const { data, error } = await supabase
          .from('patients')
          .update(payload)
          .eq('id', targetId)
          .select();

        if (!error && data && data.length > 0) {
          patientUpdated = true;
          finalId = data[0].id;
        } else if (error) {
          console.warn('Update by ID warning:', error);
        }
      }

      // 2. If not updated by ID, try updating by DNI
      if (!patientUpdated && targetDni) {
        const { data, error } = await supabase
          .from('patients')
          .update(payload)
          .eq('dni', String(targetDni).trim())
          .select();

        if (!error && data && data.length > 0) {
          patientUpdated = true;
          finalId = data[0].id;
        } else if (error) {
          console.warn('Update by DNI warning:', error);
        }
      }

      // 3. If still not updated, try updating by original full_name
      if (!patientUpdated && currentPatient?.full_name) {
        const { data, error } = await supabase
          .from('patients')
          .update(payload)
          .ilike('full_name', currentPatient.full_name.trim())
          .select();

        if (!error && data && data.length > 0) {
          patientUpdated = true;
          finalId = data[0].id;
        }
      }

      // 4. If still not updated (spontaneous patient missing from patients table), insert record
      if (!patientUpdated) {
        const { data: newPat, error: insertErr } = await supabase
          .from('patients')
          .insert([{ ...payload, professional_name: currentPatient?.professional_name || 'Consulta General' }])
          .select();

        if (insertErr) throw insertErr;
        if (newPat && newPat.length > 0) {
          finalId = newPat[0].id;
        }
      }

      // 5. Synchronize patient changes with appointments table in 4 fallback passes
      const apptUpdateObj = {
        patient_id: finalId,
        patient_name: payload.full_name,
        patient_dni: payload.dni,
        patient_phone: payload.phone,
        patient_email: payload.email
      };

      const originalDni = currentPatient?.dni || currentPatient?.patient_dni;
      const originalName = currentPatient?.full_name || currentPatient?.patient_name;

      if (finalId) {
        await supabase.from('appointments').update(apptUpdateObj).eq('patient_id', finalId);
      }
      if (originalDni) {
        await supabase.from('appointments').update(apptUpdateObj).eq('patient_dni', String(originalDni).trim());
      }
      if (payload.dni && payload.dni !== originalDni) {
        await supabase.from('appointments').update(apptUpdateObj).eq('patient_dni', String(payload.dni).trim());
      }
      if (originalName) {
        await supabase.from('appointments').update(apptUpdateObj).ilike('patient_name', String(originalName).trim());
      }

      // Sincronizar study_details JSONB en todos los turnos del paciente para mantener la consistencia total
      try {
        let apptsToSync = [];
        if (finalId) {
          const { data: d1 } = await supabase.from('appointments').select('id, study_details').eq('patient_id', finalId);
          if (d1) apptsToSync.push(...d1);
        }
        if (originalDni) {
          const { data: d2 } = await supabase.from('appointments').select('id, study_details').eq('patient_dni', String(originalDni).trim());
          if (d2) apptsToSync.push(...d2);
        }
        if (originalName) {
          const { data: d3 } = await supabase.from('appointments').select('id, study_details').ilike('patient_name', String(originalName).trim());
          if (d3) apptsToSync.push(...d3);
        }

        // Deduplicar turnos encontrados por id
        const uniqueAppts = Array.from(new Map(apptsToSync.map(item => [item.id, item])).values());

        for (const appItem of uniqueAppts) {
          const updatedStudyDetails = {
            ...(appItem.study_details || {}),
            patient_dni: payload.dni,
            insurance_provider: payload.insurance_provider || appItem.study_details?.insurance_provider
          };
          await supabase.from('appointments').update({ 
            patient_id: finalId,
            patient_dni: payload.dni,
            study_details: updatedStudyDetails 
          }).eq('id', appItem.id);
        }
      } catch (syncErr) {
        console.warn('Error al actualizar study_details en turnos:', syncErr);
      }

      const updatedPatient = {
        ...currentPatient,
        ...payload,
        id: finalId || currentPatient?.id
      };

      setCurrentPatient(updatedPatient);
      setIsEditingDatos(false);
      addNotification('Datos del paciente actualizados con éxito en la ficha y en todas las agendas', 'success');

      fetchAppointments(payload.dni || targetDni);
    } catch (err) {
      console.error('Error saving patient datos:', err);
      addNotification('Error al guardar cambios: ' + err.message, 'error');
    } finally {
      setSavingDatos(false);
    }
  };

  const handleStartEditingEv = (ev) => {
    setEditingEvId(ev.id);
    setEditingEvContent(ev.content || '');
  };

  const handleSaveEv = async (evId) => {
    if (!editingEvContent.trim()) {
      addNotification('La nota clínica no puede estar vacía', 'error');
      return;
    }
    setSavingEvChange(true);
    try {
      const { error } = await supabase
        .from('medical_evolutions')
        .update({ content: editingEvContent })
        .eq('id', evId);

      if (error) throw error;
      addNotification('Evolución clínica actualizada con éxito', 'success');
      setEditingEvId(null);
      fetchEvolutions();
    } catch (err) {
      addNotification('Error al actualizar evolución: ' + err.message, 'error');
    } finally {
      setSavingEvChange(false);
    }
  };

  const handleDeletePatient = async () => {
    if (!currentPatient) return;

    const confirmName = window.prompt(
      `⚠️ ¿ESTÁS SEGURO DE ELIMINAR ESTA FICHA DE PACIENTE?\n\n` +
      `Paciente: ${currentPatient.full_name}\n` +
      `DNI: ${currentPatient.dni || 'Sin DNI'}\n\n` +
      `Para borrarla permanentemente, escribe ELIMINAR a continuación:`
    );

    if (!confirmName || confirmName.trim().toUpperCase() !== 'ELIMINAR') {
      if (confirmName !== null) {
        addNotification('Eliminación cancelada. Debes escribir ELIMINAR para confirmar.', 'warning');
      }
      return;
    }

    try {
      const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
      const activeUser = JSON.parse(localStorage.getItem('gdt_active_user') || '{}');
      const userEmail = activeStaff.full_name || activeUser.email || 'Sistema';

      // Audit Log
      await supabase.from('audit_logs').insert([{
        action: 'BORRADO PACIENTE',
        details: {
          patient_id: currentPatient.id,
          full_name: currentPatient.full_name,
          dni: currentPatient.dni,
          insurance_provider: currentPatient.insurance_provider
        },
        user_email: userEmail
      }]);

      if (currentPatient.id) {
        const { error: deleteErr } = await supabase.from('patients').delete().eq('id', currentPatient.id);
        if (deleteErr) throw deleteErr;
      } else if (currentPatient.dni) {
        const { error: deleteErr } = await supabase.from('patients').delete().eq('dni', currentPatient.dni);
        if (deleteErr) throw deleteErr;
      }

      addNotification(`Ficha del paciente "${currentPatient.full_name}" eliminada correctamente.`, 'success');
      if (onClose) onClose();
    } catch (err) {
      console.error('Error deleting patient:', err);
      addNotification('Error al eliminar paciente: ' + err.message, 'error');
    }
  };

  const renderSidebarContent = () => {
    const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
    const activeUser = JSON.parse(localStorage.getItem('gdt_active_user') || '{}');
    const isUserRoleUser = activeUser?.role === 'user';
    const isUserRoleAdmin = activeUser?.role === 'admin' || activeUser?.role === 'administrador' || activeUser?.role === 'secretaria' || activeUser?.role === 'webmaster';
    const isAdmin = isUserRoleUser 
      ? false 
      : (isUserRoleAdmin || activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o' || (staffName && staffName.toLowerCase().includes('administracion')));

    return (
      <div className="tab-pane">
        <div className="datos-pane-header" style={{ display: 'flex', justifyContent: isAdmin ? 'space-between' : 'flex-end', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '8px' }}>
          {isAdmin && (
            <button 
              className="btn-toggle-edit" 
              onClick={handleDeletePatient} 
              style={{ fontSize: '0.75rem', padding: '0.4rem 0.8rem', background: 'rgba(255, 82, 82, 0.15)', color: '#ff5252', border: '1px solid rgba(255, 82, 82, 0.3)' }}
              title="Eliminar esta ficha de paciente permanentemente"
            >
              <Trash2 size={14} /> ELIMINAR FICHA
            </button>
          )}
          {!isEditingDatos && (
            <button className="btn-toggle-edit" onClick={handleStartEditingDatos} style={{ fontSize: '0.75rem', padding: '0.4rem 0.8rem' }}>
              <Edit3 size={14} /> EDITAR
            </button>
          )}
        </div>

            {isEditingDatos ? (
              <div className="patient-edit-form-wrapper">
                <div className="detail-section card glass-panel editing">
                  <div className="section-header-wr">
                    <User size={16} /> <h3>Editar Ficha Identificatoria</h3>
                  </div>
                  <div className="detail-grid editing">
                    <div className="info-item-edit">
                      <label>Nombre Completo</label>
                      <input 
                        type="text" 
                        value={editName} 
                        onChange={(e) => setEditName(e.target.value)} 
                        placeholder="Ej: Juan Pérez"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>DNI / Documento</label>
                      <input 
                        type="text" 
                        value={editDni} 
                        onChange={(e) => setEditDni(e.target.value)} 
                        placeholder="Ej: 30123456"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Fecha de Nacimiento</label>
                      <input 
                        type="date" 
                        value={editBirthdate} 
                        onChange={(e) => setEditBirthdate(e.target.value)} 
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Obra Social</label>
                      <input 
                        type="text" 
                        value={editInsurance} 
                        onChange={(e) => setEditInsurance(e.target.value)} 
                        placeholder="Ej: OSDE, PAMI..."
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Nº de Afiliado</label>
                      <input 
                        type="text" 
                        value={editAffiliateNumber} 
                        onChange={(e) => setEditAffiliateNumber(e.target.value)} 
                        placeholder="Ej: 123456789"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Tipo de Paciente</label>
                      <select 
                        value={editPatientType} 
                        onChange={(e) => setEditPatientType(e.target.value)}
                        style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--border-glass)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
                      >
                        <option value="Ambulatorio">Ambulatorio</option>
                        <option value="Internado">Internado</option>
                        <option value="A Domicilio">A Domicilio</option>
                        <option value="Telemedicina">Telemedicina</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="detail-section card glass-panel editing" style={{ marginTop: '1.5rem' }}>
                  <div className="section-header-wr">
                    <Phone size={16} /> <h3>Editar Datos de Contacto</h3>
                  </div>
                  <div className="detail-grid editing">
                    <div className="info-item-edit">
                      <label>Celular / WhatsApp</label>
                      <input 
                        type="text" 
                        value={editPhone} 
                        onChange={(e) => setEditPhone(e.target.value)} 
                        placeholder="Ej: +54 9 11..."
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Email</label>
                      <input 
                        type="email" 
                        value={editEmail} 
                        onChange={(e) => setEditEmail(e.target.value)} 
                        placeholder="Ej: correo@paciente.com"
                      />
                    </div>
                    <div className="info-item-edit full">
                      <label>Dirección</label>
                      <input 
                        type="text" 
                        value={editAddress} 
                        onChange={(e) => setEditAddress(e.target.value)} 
                        placeholder="Dirección, piso, depto..."
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Ciudad</label>
                      <input 
                        type="text" 
                        value={editCity} 
                        onChange={(e) => setEditCity(e.target.value)} 
                        placeholder="Ej: CABA"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Provincia</label>
                      <input 
                        type="text" 
                        value={editProvince} 
                        onChange={(e) => setEditProvince(e.target.value)} 
                        placeholder="Ej: Buenos Aires"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>País</label>
                      <input 
                        type="text" 
                        value={editCountry} 
                        onChange={(e) => setEditCountry(e.target.value)} 
                        placeholder="Ej: Argentina"
                      />
                    </div>
                    <div className="info-item-edit full" style={{ marginTop: '0.5rem' }}>
                      <label>Abogado / Estudio Jurídico</label>
                      <input 
                        type="text" 
                        value={editLawyer} 
                        onChange={(e) => setEditLawyer(e.target.value)} 
                        placeholder="Nombre del abogado o estudio jurídico..."
                      />
                    </div>
                    <div className="info-item-edit full" style={{ marginTop: '0.5rem' }}>
                      <label>Observaciones / Antecedentes</label>
                      <textarea 
                        rows={3}
                        value={editMedicalNotes} 
                        onChange={(e) => setEditMedicalNotes(e.target.value)} 
                        placeholder="Observaciones clínicas, legales, antecedentes..."
                        style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--border-glass)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="edit-actions-bar">
                  <button className="btn-cancel-edit" disabled={savingDatos} onClick={() => setIsEditingDatos(false)}>
                    CANCELAR
                  </button>
                  <button className="btn-save-edit" disabled={savingDatos} onClick={handleSaveDatos}>
                    {savingDatos ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    {savingDatos ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="patient-read-only-wrapper">
                <div className="detail-section card glass-panel">
                  <div className="section-header-wr">
                    <User size={16} /> <h3>Ficha Identificatoria</h3>
                  </div>
                  <div className="detail-grid">
                    <div className="info-item">
                      <label>Nombre Completo</label>
                      <div className="value">{currentPatient.full_name}</div>
                    </div>
                    <div className="info-item">
                      <label>DNI / Documento</label>
                      <div className="value">{currentPatient.dni || '---'}</div>
                    </div>
                    <div className="info-item">
                      <label>Fecha de Nacimiento</label>
                      <div className="value">
                        {(() => {
                          if (!currentPatient.birthdate) return 'No especificada';
                          const d = new Date(currentPatient.birthdate + 'T00:00:00');
                          return isNaN(d) ? currentPatient.birthdate : format(d, 'dd/MM/yyyy');
                        })()}
                      </div>
                    </div>
                    <div className="info-item">
                      <label>Obra Social</label>
                      <div className="value">{currentPatient.insurance_provider || 'Particular'}</div>
                    </div>
                    <div className="info-item">
                      <label>Nº de Afiliado</label>
                      <div className="value">{currentPatient.affiliate_number || '---'}</div>
                    </div>
                    <div className="info-item">
                      <label>Tipo de Paciente</label>
                      <div className="value">
                        <span style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#60a5fa', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                          {currentPatient.patient_type || 'Ambulatorio'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="detail-section card glass-panel" style={{ marginTop: '1.5rem' }}>
                  <div className="section-header-wr">
                    <Phone size={16} /> <h3>Contacto, Abogado y Observaciones</h3>
                  </div>
                  <div className="detail-grid">
                    <div className="info-item">
                      <label>Celular / WhatsApp</label>
                      <div className="value">{currentPatient.phone || '---'}</div>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <div className="value">{currentPatient.email || '---'}</div>
                    </div>
                    <div className="info-item full">
                      <label>Dirección Física</label>
                      <div className="value">
                        {currentPatient.address 
                          ? `${currentPatient.address}${currentPatient.city ? `, ${currentPatient.city}` : ''}${currentPatient.province ? ` (${currentPatient.province})` : ''}` 
                          : 'Sin especificar'}
                      </div>
                    </div>
                    <div className="info-item full" style={{ marginTop: '0.5rem' }}>
                      <label>Abogado / Estudio Jurídico</label>
                      <div className="value" style={{ color: currentPatient.preferences || currentPatient.lawyer ? 'var(--primary-cyan)' : 'inherit' }}>
                        {currentPatient.preferences || currentPatient.lawyer || 'Sin especificar'}
                      </div>
                    </div>
                    <div className="info-item full" style={{ marginTop: '0.5rem' }}>
                      <label>Observaciones / Antecedentes</label>
                      <div className="value" style={{ background: 'rgba(255,255,255,0.03)', padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--border-glass)', minHeight: '40px', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                        {currentPatient.medical_notes || <span style={{ opacity: 0.5 }}>- Sin observaciones registradas -</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
  };

  const renderMainContent = () => {
    switch (activeTab) {
      case 'evoluciones':
        return (
          <div className="tab-pane evolutions-pane">
            <div className="new-note-box card glass-panel">
              <textarea 
                placeholder="Escribir nueva evolución o nota clínica..." 
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <div className="note-actions">
                <button 
                  className="btn-save-note" 
                  disabled={savingNote || !newNote.trim()} 
                  onClick={handleAddEvolution}
                >
                  {savingNote ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                  REGISTRAR EVOLUCIÓN
                </button>
              </div>
            </div>

            <div className="timeline-v2">
              {evolutions.length === 0 ? (
                <div className="empty-timeline">No hay registros clínicos previos.</div>
              ) : evolutions.map(ev => (
                <div key={ev.id} className="timeline-item-v2">
                  <div className="item-marker"></div>
                  <div className="item-content card glass-panel">
                    <div className="item-header">
                      <span className="item-date">{format(new Date(ev.created_at), "d 'de' MMMM, yyyy", { locale: es })}</span>
                      <span className="item-prof">
                        {ev.professional_name}
                        {ev.created_by_user && (
                          <span style={{ fontSize: '0.8em', color: 'var(--accent)', marginLeft: '8px' }}>
                            (Op: {ev.created_by_user})
                          </span>
                        )}
                      </span>
                      
                      {editingEvId !== ev.id && (
                        <button className="btn-edit-ev-trigger" title="Editar evolución" onClick={() => handleStartEditingEv(ev)}>
                          <Edit3 size={14} />
                        </button>
                      )}
                    </div>
                    
                    {editingEvId === ev.id ? (
                      <div className="ev-edit-box">
                        <textarea 
                          value={editingEvContent}
                          onChange={(e) => setEditingEvContent(e.target.value)}
                          rows="4"
                        />
                        <div className="ev-edit-actions">
                          <button className="btn-cancel-ev-edit" disabled={savingEvChange} onClick={() => setEditingEvId(null)}>
                            CANCELAR
                          </button>
                          <button className="btn-save-ev-edit" disabled={savingEvChange || !editingEvContent.trim()} onClick={() => handleSaveEv(ev.id)}>
                            {savingEvChange ? <Loader2 className="animate-spin" size={14} /> : 'GUARDAR'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="item-text">{ev.content}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'turnos':
        const todayISODate = new Date().toISOString().split('T')[0];
        const pastAppointments = appointments.filter(a => (a.appointment_date < todayISODate) || a.status === 'atendido' || a.status === 'completado');
        const futureAppointments = appointments.filter(a => (a.appointment_date >= todayISODate) && a.status !== 'atendido' && a.status !== 'completado');
        
        const displayedAppointments = turnosFilterTab === 'pasados' 
          ? pastAppointments 
          : turnosFilterTab === 'futuros' 
            ? futureAppointments 
            : appointments;

        return (
          <div className="tab-pane">
            <div className="section-header-wr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={18} color="var(--primary-cyan)" /> 
                <h3 style={{ margin: 0, color: 'var(--text-bright)' }}>Historial de Turnos de {currentPatient.full_name}</h3>
              </div>
              
              <div className="turnos-filter-pill-container">
                <button 
                  type="button"
                  className={`turnos-filter-pill ${turnosFilterTab === 'pasados' ? 'active' : ''}`}
                  onClick={() => setTurnosFilterTab('pasados')}
                >
                  TURNOS PASADOS ({pastAppointments.length})
                </button>
                <button 
                  type="button"
                  className={`turnos-filter-pill ${turnosFilterTab === 'futuros' ? 'active' : ''}`}
                  onClick={() => setTurnosFilterTab('futuros')}
                >
                  TURNOS FUTUROS ({futureAppointments.length})
                </button>
                <button 
                  type="button"
                  className={`turnos-filter-pill ${turnosFilterTab === 'todos' ? 'active' : ''}`}
                  onClick={() => setTurnosFilterTab('todos')}
                >
                  TODOS ({appointments.length})
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {displayedAppointments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', color: 'var(--text-muted)' }}>
                  No hay turnos registrados en esta categoría.
                </div>
              ) : (
                displayedAppointments.map(app => {
                  const isExpanded = expandedAppId === app.id;
                  const formattedDate = (() => {
                    if (!app.appointment_date) return 'Sin fecha';
                    const d = new Date(app.appointment_date + 'T00:00:00');
                    return isNaN(d) ? app.appointment_date : format(d, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es });
                  })();

                  const statusColor = app.status === 'atendido' ? '#00e5ff' : app.status === 'confirmado' ? '#00ff7f' : app.status === 'cancelado' ? '#ff5252' : '#ffca28';

                  let detailsObj = app.study_details || {};
                  if (typeof detailsObj === 'string') {
                    try { detailsObj = JSON.parse(detailsObj); } catch (e) { detailsObj = {}; }
                  }
                  const attached = detailsObj.attached_files || app.files || [];

                  return (
                    <div 
                      key={app.id} 
                      style={{ 
                        background: 'var(--bg-lighter)', 
                        border: '1px solid var(--border-glass)', 
                        borderRadius: '10px', 
                        overflow: 'hidden',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div 
                        onClick={() => setExpandedAppId(isExpanded ? null : app.id)}
                        style={{ 
                          padding: '0.8rem 1.2rem', 
                          display: 'flex', 
                          justify: 'space-between', 
                          alignItems: 'center', 
                          cursor: 'pointer',
                          background: isExpanded ? 'rgba(0, 229, 255, 0.06)' : 'transparent',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-bright)', textTransform: 'capitalize' }}>
                            {formattedDate}
                          </span>
                          <span style={{ fontSize: '0.85rem', color: 'var(--primary-cyan)', background: 'rgba(0, 229, 255, 0.1)', padding: '2px 8px', borderRadius: '4px' }}>
                            ⏰ {app.appointment_time?.substring(0, 5) || '00:00'} hs
                          </span>
                          <span style={{ fontSize: '0.85rem', color: '#ffca28', fontWeight: 600 }}>
                            🏥 {app.professional_name || 'CONSULTORIOS EXTERNOS'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: '12px', background: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}44` }}>
                            {app.status?.toUpperCase() || 'PENDIENTE'}
                          </span>
                          {isExpanded ? <ChevronUp size={18} color="var(--primary-cyan)" /> : <ChevronDown size={18} color="var(--text-muted)" />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ padding: '1rem 1.2rem', borderTop: '1px solid var(--border-glass)', background: 'rgba(0,0,0,0.2)', fontSize: '0.85rem' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                            <div>
                              <strong style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.75rem' }}>ESTUDIO / ATENCIÓN</strong>
                              <span style={{ color: 'var(--text-bright)', fontWeight: 600 }}>{app.study_type || 'Consulta General'}</span>
                            </div>
                            <div>
                              <strong style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.75rem' }}>COBERTURA / PLAN</strong>
                              <span style={{ color: '#ffd54f' }}>
                                {detailsObj.insurance_provider || currentPatient.insurance_provider || 'PARTICULAR'} {detailsObj.insurance_plan ? `(${detailsObj.insurance_plan})` : ''}
                              </span>
                            </div>
                            <div>
                              <strong style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.75rem' }}>MONTO / PRECIO</strong>
                              <span style={{ color: '#00ff7f', fontWeight: 600 }}>$ {app.price ? Number(app.price).toLocaleString('es-AR') : '0,00'}</span>
                            </div>
                            <div>
                              <strong style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.75rem' }}>OTORGADO / ATENDIDO POR</strong>
                              <span style={{ color: 'var(--primary-cyan)' }}>{app.created_by_user || 'Sistema'}</span>
                            </div>
                          </div>

                          <div style={{ marginTop: '10px', background: 'rgba(255,255,255,0.03)', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <strong style={{ color: '#ffca28', display: 'block', fontSize: '0.75rem', marginBottom: '4px' }}>
                              📝 OBSERVACIONES Y DETALLES DE LO REALIZADO EN ESTA ATENCIÓN:
                            </strong>
                            <p style={{ margin: 0, color: 'var(--text-main)', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                              {app.admin_notes || app.notes || detailsObj.reason || app.patient_message || '- Sin observaciones registradas en este turno -'}
                            </p>
                          </div>

                          {attached.length > 0 && (
                            <div style={{ marginTop: '10px' }}>
                              <strong style={{ color: 'var(--primary-cyan)', display: 'block', fontSize: '0.75rem', marginBottom: '4px' }}>
                                📎 ORDENES Y ARCHIVOS ADJUNTOS DE ESTA ATENCIÓN ({attached.length}):
                              </strong>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {attached.map((f, i) => (
                                  <a key={i} href={f.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0, 229, 255, 0.1)', color: '#00e5ff', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', textDecoration: 'none', border: '1px solid rgba(0,229,255,0.2)' }}>
                                    <Paperclip size={12} /> {f.name || `Archivo ${i+1}`}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      case 'archivos':
        const patientFiles = (() => {
          const files = [];
          appointments.forEach(app => {
            const details = app.study_details || {};
            const attached = details.attached_files || [];
            attached.forEach(file => {
              files.push({
                ...file,
                appointmentDate: app.appointment_date,
                studyType: app.study_type,
                professionalName: app.professional_name
              });
            });
          });
          return files;
        })();
        return (
          <div className="tab-pane">
            <div className="section-header-wr">
              <Paperclip size={16} /> <h3>Documentación Adjunta</h3>
            </div>
            <div className="patient-files-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
              {patientFiles.length === 0 ? (
                <div className="empty-timeline" style={{ gridColumn: '1 / -1' }}>El paciente no registra archivos adjuntos.</div>
              ) : patientFiles.map((file, idx) => (
                <div key={idx} className="file-item-premium card glass-panel" style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px', background: 'rgba(15, 23, 42, 0.25)' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', overflow: 'hidden' }}>
                    <div className="file-icon orange" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(230, 126, 34, 0.1)', color: '#e67e22', flexShrink: 0 }}>
                      <FileText size={18} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <span className="file-name" style={{ color: 'white', fontWeight: '600', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>{file.name}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {file.study_name && file.study_name !== 'General' ? file.study_name : (file.studyType || 'Consulta')}
                      </span>
                      <span className="file-date">
                        Turno: {(() => {
                          if (!file.appointmentDate) return '---';
                          const d = new Date(file.appointmentDate + 'T00:00:00');
                          return isNaN(d) ? file.appointmentDate : format(d, 'dd/MM/yyyy');
                        })()}
                      </span>
                    </div>
                  </div>
                  {file.url && (
                    <a href={file.url} target="_blank" rel="noreferrer" className="btn-view-file" style={{ background: 'rgba(0, 229, 255, 0.1)', color: '#00e5ff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'none', cursor: 'pointer', flexShrink: 0 }}>
                      VER
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className="patient-detail-overlay" 
      onClick={onClose}
      style={{ 
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
        zIndex: 99999, 
        backgroundColor: 'rgba(11, 11, 20, 0.85)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        backdropFilter: 'blur(12px)'
      }}
    >
      <div className="patient-detail-container glass-modal-premium split-layout" onClick={e => e.stopPropagation()} style={{ zIndex: 100000, position: 'relative' }}>
        
        <div className="pd-sidebar">
          <div className="pd-sidebar-header">
            <button className="btn-close-p" onClick={onClose}><X size={20} /></button>
            <div className="p-avatar-lg">{currentPatient.full_name?.trim() ? currentPatient.full_name.trim().charAt(0).toUpperCase() : 'P'}</div>
            <div className="p-name-meta" style={{ textAlign: 'center' }}>
              <h2>{currentPatient.full_name}</h2>
              <div className="p-meta-tags" style={{ justifyContent: 'center' }}>
                <span>DNI: {currentPatient.dni || '---'}</span>
                <span className="dot">•</span>
                <span>{currentPatient.insurance_provider || 'Particular'}</span>
              </div>
            </div>
          </div>
          <div style={{ padding: '0 2rem 1.5rem 2rem' }}>
              <button className="btn-agendar-turno-sidebar" onClick={handleNewAppointment} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: 'var(--primary-cyan)', color: '#0f172a', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(0, 191, 165, 0.3)' }}>
                <Calendar size={18} /> AGENDAR TURNO
              </button>
            </div>
          <div className="pd-sidebar-content">
            {renderSidebarContent()}
          </div>
        </div>

        <div className="pd-main">
          <div className="detail-tabs-premium">
            <button className={`tab-btn-p ${activeTab === 'evoluciones' ? 'active' : ''}`} onClick={() => setActiveTab('evoluciones')}>
              <History size={18} /> HISTORIA CLÍNICA
            </button>
            <button className={`tab-btn-p ${activeTab === 'turnos' ? 'active' : ''}`} onClick={() => setActiveTab('turnos')}>
              <Calendar size={18} /> TURNOS
            </button>
            <button className={`tab-btn-p ${activeTab === 'archivos' ? 'active' : ''}`} onClick={() => setActiveTab('archivos')}>
              <Paperclip size={18} /> ARCHIVOS
            </button>
          </div>

          <div className="detail-body-premium">
            {renderMainContent()}
          </div>
        </div>
      </div>
      <AppointmentModal isOpen={isAppModalOpen} onClose={() => setIsAppModalOpen(false)} initialData={appModalData} onSave={handleSaveAppointment} professionalsList={professionals} />
    </div>
  );
};


const PatientDetail = (props) => (
  <ErrorBoundary>
    <PatientDetailInner {...props} />
  </ErrorBoundary>
);

export default PatientDetail;
